/**
 * Thin fetch wrapper that sends credentials (cookies) and handles the
 * "401 → bounce to login" case in one place. Every page uses this.
 */

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });

  if (res.status === 401) {
    // Session missing or expired. Send the user to the branded /login page
    // (the admin sign-in screen with the "Sign in with Microsoft" button), NOT
    // straight to /auth/login — otherwise the IdP redirect skips the sign-in
    // page entirely. /login preserves `next` and the button hits /auth/login.
    // Guard against a loop if we're already on /login.
    if (window.location.pathname !== '/login') {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?next=${next}`;
    }
    throw new Error('Unauthorized');
  }

  if (res.status === 403) {
    // Signed in but not an oakallow admin. Show a clear message instead of
    // looping through /auth/login forever.
    if (window.location.pathname !== '/login') {
      window.location.href = '/login?error=not_admin';
    }
    throw new Error('Forbidden');
  }

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${body || res.statusText}`);
  }

  return res.json() as Promise<T>;
}
